<script setup>
// Auto-extracted chrome for PvKostenrechner.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "PV-Kostenrechner" },
    brandTagline: { type: String, default: "Kosten, Förderung, Amortisation - ehrlich." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "PV-Kostenrechner");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Kosten, Förderung, Amortisation - ehrlich.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap";
</script>

<template>
    <div class="pvk" :style="`--pvk-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="pvk-rail">
                    <div class="pvk-rail__inner">
                        <a class="pvk-mark" href="/" aria-label="Startseite">
                            <span class="pvk-mark__sigil" aria-hidden="true">
                                <span class="pvk-mark__sun"></span>
                                <span class="pvk-mark__beam pvk-mark__beam--1"></span>
                                <span class="pvk-mark__beam pvk-mark__beam--2"></span>
                                <span class="pvk-mark__beam pvk-mark__beam--3"></span>
                            </span>
                            <span class="pvk-mark__words">
                                <span class="pvk-mark__name">{{ brandName }}</span>
                                <span class="pvk-mark__tag">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="pvk-rail__nav" aria-label="Hauptnavigation">
                            <a href="#methodik">Methodik</a>
                            <a href="#faq">Fragen</a>
                            <a href="/">Übersicht</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="pvk-foot">
                    <div class="pvk-foot__inner">
                        <p>&copy; {{ new Date().getFullYear() }} {{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav>
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* PVK pilot 2026-05. pvk-* namespace (AGENT.md DOM signature uniqueness).
   No shared components. Inter body + Lora display, amber accent. Distinct
   from wpa-* (EignungsAmpel): sun sigil, gauge ring, KPI quad grid. */

.pvk {
    --pvk-ink: #1c1610;
    --pvk-ink-2: #4a3c2c;
    --pvk-mute: #807060;
    --pvk-page: #fbf6ec;
    --pvk-surface: #ffffff;
    --pvk-line: #ead9bf;
    --pvk-line-2: #d6c19f;
    --pvk-warn: #b91c1c;
    background: var(--pvk-page);
    color: var(--pvk-ink);
    font-family: 'Inter', system-ui, -apple-system, Segoe UI, sans-serif;
    font-size: 16px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    min-height: 100dvh;
}
.pvk * { box-sizing: border-box; }
.pvk h1, .pvk h2, .pvk h3 { font-family: 'Lora', 'Fraunces', Georgia, serif; font-weight: 500; letter-spacing: -0.015em; }
.pvk a { color: inherit; }

/* Subtle radial sun halo top-right */
.pvk::before {
    content: '';
    position: fixed; inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 90% -10%, rgba(202, 138, 4, 0.10), transparent 45%);
    z-index: 1;
}

/* ── Rail ───────────────────────────────────────────────────────────── */
.pvk-rail { border-bottom: 1px solid var(--pvk-line); background: var(--pvk-page); position: relative; z-index: 2; }
.pvk-rail__inner { max-width: 1240px; margin: 0 auto; padding: 18px 32px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.pvk-mark { display: inline-flex; align-items: center; gap: 14px; text-decoration: none; }
.pvk-mark__sigil { position: relative; width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; }
.pvk-mark__sun { width: 14px; height: 14px; border-radius: 50%; background: var(--pvk-accent); box-shadow: 0 0 0 2px rgba(202,138,4,0.18); }
.pvk-mark__beam { position: absolute; width: 2px; height: 8px; background: var(--pvk-accent); top: 4px; left: 16px; border-radius: 1px; opacity: 0.85; }
.pvk-mark__beam--1 { transform-origin: 1px 13px; transform: rotate(0deg); }
.pvk-mark__beam--2 { transform-origin: 1px 13px; transform: rotate(90deg); }
.pvk-mark__beam--3 { transform-origin: 1px 13px; transform: rotate(-90deg); }
.pvk-mark__words { display: flex; flex-direction: column; line-height: 1.1; }
.pvk-mark__name { font-family: 'Lora', serif; font-weight: 500; font-size: 19px; }
.pvk-mark__tag { font-size: 11px; color: var(--pvk-mute); letter-spacing: 0.08em; text-transform: uppercase; margin-top: 3px; }
.pvk-rail__nav { display: flex; gap: 28px; }
.pvk-rail__nav a { color: var(--pvk-ink-2); text-decoration: none; font-size: 13px; position: relative; transition: color 200ms; }
.pvk-rail__nav a:hover { color: var(--pvk-accent); }

/* ── Stage ──────────────────────────────────────────────────────────── */
.pvk-stage { position: relative; z-index: 2; padding: 96px 0 56px; }
.pvk-stage__inner { max-width: 1240px; margin: 0 auto; padding: 0 32px; display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 64px; align-items: end; }
@media (max-width: 920px) { .pvk-stage { padding: 56px 0 40px; } .pvk-stage__inner { grid-template-columns: 1fr; gap: 40px; align-items: start; } }

.pvk-eyebrow { font-family: 'Inter', sans-serif; font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--pvk-accent); font-weight: 600; margin: 0 0 18px; }
.pvk-eyebrow--invert { color: rgba(255,255,255,0.70); }

.pvk-display { font-family: 'Lora', serif; font-weight: 500; font-size: clamp(44px, 6.4vw, 80px); line-height: 0.98; letter-spacing: -0.025em; margin: 0; color: var(--pvk-ink); text-wrap: balance; }
.pvk-display__l1 { display: block; }
.pvk-display__l2 { display: block; }
.pvk-display__l2 em { font-style: italic; font-weight: 500; color: var(--pvk-accent); }
.pvk-display__l3 { display: block; font-style: italic; color: var(--pvk-ink-2); }
.pvk-deck { margin: 28px 0 0; font-size: 17px; line-height: 1.6; color: var(--pvk-ink-2); max-width: 58ch; }

/* meter (live KfW/BAFA card) */
.pvk-meter { background: var(--pvk-surface); border: 1px solid var(--pvk-line); border-radius: 6px; padding: 26px; box-shadow: 0 1px 0 rgba(28,22,16,0.04); }
.pvk-meter__head { font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--pvk-mute); margin: 0 0 18px; }
.pvk-meter__list { margin: 0; display: grid; gap: 14px; }
.pvk-meter__list div { display: grid; grid-template-columns: 88px 1fr; gap: 14px; align-items: baseline; padding-bottom: 14px; border-bottom: 1px dashed var(--pvk-line); }
.pvk-meter__list div:last-child { border-bottom: 0; padding-bottom: 0; }
.pvk-meter__list dt { font-family: 'Lora', serif; font-weight: 500; color: var(--pvk-ink); font-size: 14px; }
.pvk-meter__list dd { margin: 0; font-size: 13px; color: var(--pvk-ink-2); }
.pvk-meter__note { margin: 18px 0 0; font-size: 11px; color: var(--pvk-mute); letter-spacing: 0.02em; }

/* citebar */
.pvk-citebar { list-style: none; padding: 0; margin: 72px auto 0; max-width: 1240px; padding: 28px 32px 0; display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; border-top: 1px solid var(--pvk-line); }
@media (max-width: 920px) { .pvk-citebar { grid-template-columns: 1fr; gap: 18px; margin-top: 40px; } }
.pvk-citebar li { display: grid; grid-template-columns: auto 1fr; gap: 14px; font-size: 13px; line-height: 1.6; color: var(--pvk-ink-2); }
.pvk-citebar__no { font-family: 'Lora', serif; font-style: italic; color: var(--pvk-accent); padding-top: 2px; }
.pvk-citebar a { color: var(--pvk-accent); text-decoration: underline; text-underline-offset: 3px; }
.pvk-citebar a:hover { opacity: 0.75; }

/* ── Calc ───────────────────────────────────────────────────────────── */
.pvk-calc { position: relative; z-index: 2; padding: 88px 0; border-top: 1px solid var(--pvk-line); margin-top: 64px; background: var(--pvk-surface); }
.pvk-calc__inner { max-width: 1240px; margin: 0 auto; padding: 0 32px; display: grid; grid-template-columns: 260px 1fr; gap: 56px; align-items: start; }
@media (max-width: 920px) { .pvk-calc__inner { grid-template-columns: 1fr; gap: 32px; } }

.pvk-tabs { display: grid; gap: 6px; position: sticky; top: 24px; }
@media (max-width: 920px) { .pvk-tabs { position: static; grid-template-columns: 1fr 1fr; gap: 8px; } }
.pvk-tab { display: grid; grid-template-columns: 28px 1fr auto; gap: 14px; align-items: start; text-align: left; background: transparent; border: 0; border-left: 2px solid var(--pvk-line); padding: 16px 18px; cursor: pointer; transition: background 200ms, border-color 200ms; font-family: inherit; color: var(--pvk-ink-2); }
.pvk-tab:hover { background: rgba(202,138,4,0.04); }
.pvk-tab--on { border-left-color: var(--pvk-accent); background: rgba(202,138,4,0.08); color: var(--pvk-ink); }
.pvk-tab--done .pvk-tab__count { color: var(--pvk-accent); }
.pvk-tab__roman { font-family: 'Lora', serif; font-style: italic; font-size: 18px; color: var(--pvk-mute); }
.pvk-tab--on .pvk-tab__roman { color: var(--pvk-accent); }
.pvk-tab__body { display: flex; flex-direction: column; gap: 3px; }
.pvk-tab__name { font-weight: 600; font-size: 14px; color: var(--pvk-ink); }
.pvk-tab__sub { font-size: 12px; color: var(--pvk-mute); line-height: 1.45; }
.pvk-tab__count { font-size: 11px; font-variant-numeric: tabular-nums; color: var(--pvk-mute); letter-spacing: 0.04em; }

.pvk-chapter__head { padding-bottom: 24px; border-bottom: 1px solid var(--pvk-line); margin-bottom: 32px; }
.pvk-chapter__roman { display: block; font-family: 'Lora', serif; font-style: italic; font-size: 13px; color: var(--pvk-accent); margin-bottom: 8px; }
.pvk-chapter__title { font-size: clamp(28px, 3.6vw, 40px); margin: 0; line-height: 1.05; }
.pvk-chapter__sub { font-size: 15px; color: var(--pvk-mute); margin: 10px 0 0; max-width: 52ch; }

.pvk-chapter__body { display: grid; gap: 32px; }
.pvk-q__label { display: block; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--pvk-mute); margin-bottom: 14px; }
.pvk-q__hint { font-size: 12px; color: var(--pvk-mute); margin: 8px 0 0; }

.pvk-opts { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
.pvk-opt { text-align: left; background: var(--pvk-page); border: 1px solid var(--pvk-line); border-radius: 4px; padding: 14px 16px; cursor: pointer; font-family: inherit; color: var(--pvk-ink); transition: all 200ms; }
.pvk-opt:hover { border-color: var(--pvk-line-2); }
.pvk-opt--on { background: var(--pvk-ink); color: var(--pvk-page); border-color: var(--pvk-ink); }
.pvk-opt--on .pvk-opt__hint { color: rgba(251,246,236,0.7); }
.pvk-opt__main { display: block; font-weight: 500; font-size: 14px; }
.pvk-opt__hint { display: block; font-size: 12px; color: var(--pvk-mute); margin-top: 4px; }

.pvk-num { display: inline-flex; align-items: baseline; gap: 12px; background: var(--pvk-page); border: 1px solid var(--pvk-line); padding: 4px 16px; border-radius: 4px; transition: border-color 240ms; }
.pvk-num:focus-within { border-color: var(--pvk-accent); }
.pvk-num input { font-family: 'Lora', serif; font-size: 26px; font-weight: 500; letter-spacing: -0.01em; border: 0; outline: 0; background: transparent; padding: 12px 0; width: 140px; color: var(--pvk-ink); }
.pvk-num__unit { font-size: 13px; color: var(--pvk-mute); }

.pvk-note { font-size: 14px; color: var(--pvk-ink-2); background: rgba(202,138,4,0.06); border: 1px dashed var(--pvk-line-2); padding: 18px; border-radius: 4px; }

/* förderung preview */
.pvk-foerder { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.pvk-foerder__card { background: var(--pvk-page); border: 1px solid var(--pvk-line); border-radius: 4px; padding: 22px; }
.pvk-foerder__eyebrow { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--pvk-mute); margin: 0 0 12px; }
.pvk-foerder__big { font-family: 'Lora', serif; font-size: 32px; font-weight: 500; letter-spacing: -0.02em; color: var(--pvk-accent); margin: 0 0 8px; line-height: 1; }
.pvk-foerder__txt { font-size: 13px; color: var(--pvk-ink-2); margin: 0; line-height: 1.55; }
.pvk-foerder__note { font-size: 13px; color: var(--pvk-mute); margin: 16px 0 0; font-style: italic; }

.pvk-chapter__foot { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-top: 32px; margin-top: 32px; border-top: 1px solid var(--pvk-line); }
.pvk-link { background: transparent; border: 0; color: var(--pvk-mute); font-size: 13px; cursor: pointer; font-family: inherit; padding: 8px 0; transition: color 200ms; }
.pvk-link:hover { color: var(--pvk-accent); }
.pvk-btn { font-family: inherit; font-size: 14px; font-weight: 600; padding: 14px 24px; border-radius: 4px; border: 1px solid var(--pvk-ink); background: transparent; color: var(--pvk-ink); cursor: pointer; transition: all 200ms; }
.pvk-btn:hover:not(:disabled) { background: var(--pvk-ink); color: var(--pvk-page); }
.pvk-btn--solid { background: var(--pvk-accent); color: #fff; border-color: var(--pvk-accent); }
.pvk-btn--solid:hover:not(:disabled) { background: #a06b03; border-color: #a06b03; color: #fff; }
.pvk-btn--lg { padding: 16px 32px; font-size: 15px; }
.pvk-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pvk-err { color: var(--pvk-warn); font-size: 13px; margin: 12px 0 0; }

/* ── Result ─────────────────────────────────────────────────────────── */
.pvk-result { position: relative; z-index: 2; padding: 88px 0; background: #1c1610; color: #fbf6ec; border-top: 1px solid #1c1610; }
.pvk-result--hot  { background: linear-gradient(180deg, #3d2604 0%, #1c1610 65%); }
.pvk-result--warm { background: linear-gradient(180deg, #2f2a16 0%, #1c1610 65%); }
.pvk-result--cool { background: linear-gradient(180deg, #1d2426 0%, #1c1610 65%); }
.pvk-result__inner { max-width: 1240px; margin: 0 auto; padding: 0 32px; }

.pvk-result__verdict { display: flex; align-items: center; gap: 40px; padding-bottom: 40px; border-bottom: 1px solid rgba(251,246,236,0.12); margin-bottom: 40px; }
@media (max-width: 720px) { .pvk-result__verdict { flex-direction: column; align-items: flex-start; gap: 24px; } }
.pvk-verdict { font-size: clamp(26px, 3.6vw, 38px); margin: 0; color: #fbf6ec; line-height: 1.15; max-width: 22ch; }

/* score gauge (CSS-only conic) */
.pvk-gauge { position: relative; width: 132px; height: 132px; flex: none; }
.pvk-gauge__ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(var(--pvk-accent) var(--pvk-score-deg, 0deg), rgba(251,246,236,0.10) var(--pvk-score-deg, 0deg)); transition: background 600ms ease-out; }
.pvk-gauge__hub { position: absolute; inset: 14px; border-radius: 50%; background: #1c1610; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; }
.pvk-gauge__num { font-family: 'Lora', serif; font-size: 44px; font-weight: 500; color: #fbf6ec; letter-spacing: -0.03em; line-height: 1; }
.pvk-gauge__unit { font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(251,246,236,0.55); }

/* KPI 4-up grid (distinct from wpa 3-up stats) */
.pvk-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; margin: 0 0 40px; border-top: 1px solid rgba(251,246,236,0.12); border-bottom: 1px solid rgba(251,246,236,0.12); }
@media (max-width: 900px) { .pvk-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .pvk-kpis { grid-template-columns: 1fr; } }
.pvk-kpi { padding: 28px 24px; border-left: 1px solid rgba(251,246,236,0.10); }
.pvk-kpi:first-child { border-left: 0; }
@media (max-width: 900px) { .pvk-kpi:nth-child(3) { border-left: 0; } .pvk-kpi:nth-child(3), .pvk-kpi:nth-child(4) { border-top: 1px solid rgba(251,246,236,0.10); } }
@media (max-width: 520px) { .pvk-kpi { border-left: 0; border-top: 1px solid rgba(251,246,236,0.10); } .pvk-kpi:first-child { border-top: 0; } }
.pvk-kpi dt { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(251,246,236,0.55); margin-bottom: 12px; }
.pvk-kpi dd { margin: 0; display: flex; align-items: baseline; gap: 8px; font-family: 'Lora', serif; font-weight: 500; }
.pvk-kpi__num { font-size: 32px; letter-spacing: -0.02em; color: #fbf6ec; line-height: 1; }
.pvk-kpi__unit { font-size: 14px; color: rgba(251,246,236,0.55); }
.pvk-kpi p { margin: 12px 0 0; font-size: 12px; color: rgba(251,246,236,0.6); }

.pvk-leadform, .pvk-callout, .pvk-thanks { background: #fbf6ec; color: var(--pvk-ink); border-radius: 6px; padding: 40px; }
.pvk-leadform { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr); gap: 48px; align-items: start; }
@media (max-width: 920px) { .pvk-leadform { grid-template-columns: 1fr; gap: 32px; } }
.pvk-leadform__copy h3 { font-size: 26px; margin: 0 0 14px; line-height: 1.2; }
.pvk-leadform__copy p { color: var(--pvk-ink-2); font-size: 14px; margin: 0 0 18px; }
.pvk-leadform__bullets { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
.pvk-leadform__bullets li { font-size: 13px; color: var(--pvk-ink-2); padding-left: 18px; position: relative; }
.pvk-leadform__bullets li::before { content: '+'; position: absolute; left: 0; color: var(--pvk-accent); font-weight: 700; }

.pvk-callout h3 { font-size: 24px; margin: 0 0 14px; }
.pvk-callout p { font-size: 15px; color: var(--pvk-ink-2); margin: 0 0 18px; max-width: 64ch; }
.pvk-links { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
.pvk-links a { color: var(--pvk-accent); text-decoration: none; font-size: 14px; font-weight: 500; }
.pvk-links a:hover { opacity: 0.75; }

.pvk-form { display: grid; gap: 14px; }
.pvk-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 600px) { .pvk-form__row { grid-template-columns: 1fr; } }
.pvk-form label { display: flex; flex-direction: column; font-size: 12px; color: var(--pvk-mute); }
.pvk-form label > span { font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 6px; }
.pvk-form label > span em { font-weight: 400; text-transform: none; letter-spacing: 0; font-style: italic; }
.pvk-form input { font-family: 'Inter', sans-serif; font-size: 15px; padding: 12px 0; border: 0; border-bottom: 1px solid var(--pvk-line); background: transparent; color: var(--pvk-ink); border-radius: 0; transition: border-color 200ms; }
.pvk-form input:focus { outline: none; border-bottom-color: var(--pvk-accent); }
.pvk-consent { display: grid; grid-template-columns: 20px 1fr; gap: 12px; font-size: 11px; color: var(--pvk-mute); line-height: 1.6; padding: 16px 0; border-top: 1px dashed var(--pvk-line); margin-top: 8px; }
.pvk-consent input { width: 18px; height: 18px; margin-top: 2px; accent-color: var(--pvk-accent); }
.pvk-form__cta { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }

/* ── Method ─────────────────────────────────────────────────────────── */
.pvk-method { position: relative; z-index: 2; padding: 112px 0; }
.pvk-method__inner { max-width: 1240px; margin: 0 auto; padding: 0 32px; }
.pvk-stage__photo { margin: 0; }
.pvk-stage__photo img { width: 100%; height: auto; display: block; border: 1px solid rgba(0,0,0,0.08); }
.pvk-stage__photo figcaption { font-size: 12px; color: #64748b; margin-top: 8px; }
.pvk-method__photo { margin: 32px 0 0; }
.pvk-method__photo img { width: 100%; height: auto; display: block; border: 1px solid rgba(0,0,0,0.08); }
.pvk-method__photo figcaption { font-size: 12px; color: #64748b; margin-top: 8px; }
.pvk-h2 { font-size: clamp(34px, 4.2vw, 52px); margin: 0 0 48px; line-height: 1.05; letter-spacing: -0.025em; max-width: 18ch; }
.pvk-method__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; }
@media (max-width: 920px) { .pvk-method__grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .pvk-method__grid { grid-template-columns: 1fr; } }
.pvk-method article { padding-top: 28px; border-top: 1px solid var(--pvk-line-2); }
.pvk-method__roman { display: inline-block; font-family: 'Lora', serif; font-style: italic; font-size: 15px; color: var(--pvk-accent); margin-bottom: 14px; }
.pvk-method article h3 { font-size: 19px; line-height: 1.25; margin: 0 0 10px; max-width: 18ch; }
.pvk-method article p { font-size: 14px; color: var(--pvk-ink-2); margin: 0; line-height: 1.6; }

/* ── FAQ ────────────────────────────────────────────────────────────── */
.pvk-faq { position: relative; z-index: 2; padding: 88px 0; background: var(--pvk-surface); border-top: 1px solid var(--pvk-line); }
.pvk-faq__inner { max-width: 1080px; margin: 0 auto; padding: 0 32px; }
.pvk-faq dl { margin: 0; }
.pvk-faq__item { display: grid; grid-template-columns: 1fr 1.6fr; gap: 56px; padding: 28px 0; border-top: 1px solid var(--pvk-line); }
.pvk-faq__item:first-child { border-top: 1px solid var(--pvk-ink); }
.pvk-faq__item:last-child { border-bottom: 1px solid var(--pvk-line); }
@media (max-width: 720px) { .pvk-faq__item { grid-template-columns: 1fr; gap: 12px; } }
.pvk-faq__item dt { display: flex; gap: 18px; align-items: baseline; font-family: 'Lora', serif; font-size: 19px; font-weight: 500; line-height: 1.3; color: var(--pvk-ink); }
.pvk-faq__no { font-style: italic; color: var(--pvk-accent); font-size: 13px; flex: none; }
.pvk-faq__item dd { margin: 0; font-size: 15px; color: var(--pvk-ink-2); line-height: 1.65; }

/* ── Foot ───────────────────────────────────────────────────────────── */
.pvk-foot { position: relative; z-index: 2; background: #1c1610; color: rgba(251,246,236,0.6); padding: 28px 0; }
.pvk-foot__inner { max-width: 1240px; margin: 0 auto; padding: 0 32px; display: flex; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; flex-wrap: wrap; }
.pvk-foot a { color: rgba(251,246,236,0.7); text-decoration: none; margin-left: 24px; }
.pvk-foot a:hover { color: #fbf6ec; }

@media (prefers-reduced-motion: reduce) {
    .pvk *, .pvk *::before, .pvk *::after { transition: none !important; animation: none !important; }
}

</style>
